import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {
  DEFAULT_LIVE_STREAM_TRANSPORT,
  resolveRelayStreamUrl,
  startCameraRelay,
} from "../../api/cameras.ts";
import {
  fetchPtzLimits,
  fetchPtzState,
  ptzRelativeMove,
  ptzResetZoom,
  type PtzLimits,
  type PtzState,
} from "../../api/ptz.ts";
import { PtzGamepadDpad } from "../../components/place360/PtzGamepadDpad.tsx";
import { PtzZoomPanel } from "../../components/place360/PtzZoomPanel.tsx";
import { StreamVideo } from "../../components/place360/StreamVideo.tsx";
import {
  usePtzContinuousControl,
  type PtzDir,
} from "../../lib/usePtzContinuousControl.ts";
import "../../styles/omniWatchPlace360.css";

type PresetMappingPtzLiveViewProps = {
  cameraId: string;
};

export function PresetMappingPtzLiveView({
  cameraId,
}: PresetMappingPtzLiveViewProps) {
  const [playlistUrl, setPlaylistUrl] = useState<string | null>(null);
  const [relayLoading, setRelayLoading] = useState(true);
  const [relayError, setRelayError] = useState<string | null>(null);
  const [streamReady, setStreamReady] = useState(false);
  const [ptz, setPtz] = useState<PtzState>({ pan: 0, tilt: 0, zoom: 1 });
  const [ptzLimits, setPtzLimits] = useState<PtzLimits | null>(null);

  const ptzZoomRef = useRef(ptz.zoom);
  ptzZoomRef.current = ptz.zoom;
  const pendingZoomTargetRef = useRef<number | null>(null);
  const zoomCommitChainRef = useRef<Promise<void>>(Promise.resolve());
  const resetZoomInFlightRef = useRef(false);

  const ptzOperable = Boolean(cameraId.trim());

  useEffect(() => {
    if (!cameraId) return undefined;
    let cancelled = false;
    setRelayLoading(true);
    setRelayError(null);
    setPlaylistUrl(null);
    setStreamReady(false);

    void (async () => {
      try {
        const urls = await startCameraRelay(cameraId);
        if (cancelled) return;
        const url = resolveRelayStreamUrl(
          urls,
          "ptz",
          DEFAULT_LIVE_STREAM_TRANSPORT,
        );
        if (!url) {
          setRelayError(
            "Could not start PTZ live stream. Check the PTZ URL and relay.",
          );
          return;
        }
        setPlaylistUrl(url);
      } catch (e) {
        if (cancelled) return;
        setRelayError(
          e instanceof Error ? e.message : "Could not start PTZ stream.",
        );
      } finally {
        if (!cancelled) setRelayLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cameraId]);

  useEffect(() => {
    if (!ptzOperable || !streamReady) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await fetchPtzState(cameraId);
        if (!cancelled) setPtz(s);
      } catch {
        /* ignore poll errors */
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 900);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [cameraId, ptzOperable, streamReady]);

  useEffect(() => {
    if (!ptzOperable || !streamReady) {
      setPtzLimits(null);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const limits = await fetchPtzLimits(cameraId);
        if (!cancelled) setPtzLimits(limits);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cameraId, ptzOperable, streamReady]);

  const sliderMin = ptzLimits ? Math.round(ptzLimits.zoom.min) : 1;
  const sliderMax = ptzLimits ? Math.round(ptzLimits.zoom.max) : 25;

  const onPtzZoomCommit = useCallback(
    (target: number) => {
      if (!ptzOperable) return;
      const rounded = Math.min(
        sliderMax,
        Math.max(sliderMin, Math.round(target)),
      );
      pendingZoomTargetRef.current = rounded;
      zoomCommitChainRef.current = zoomCommitChainRef.current
        .then(async () => {
          while (pendingZoomTargetRef.current !== null) {
            const goal = pendingZoomTargetRef.current;
            pendingZoomTargetRef.current = null;
            const cur = Math.round(ptzZoomRef.current);
            if (goal === cur) continue;
            try {
              const next = await ptzRelativeMove(cameraId, {
                dZoom: goal - cur,
              });
              ptzZoomRef.current = next.zoom;
              setPtz(next);
            } catch {
              return;
            }
          }
        })
        .catch(() => undefined);
    },
    [cameraId, ptzOperable, sliderMin, sliderMax],
  );

  const ptzContinuous = usePtzContinuousControl({
    cameraId,
    operable: ptzOperable,
    onResult: (next) => setPtz(next),
  });

  const atLimit = useMemo(() => {
    if (!ptzLimits) {
      return {
        left: false,
        right: false,
        up: false,
        down: false,
        zoomIn: false,
        zoomOut: false,
      };
    }
    const eps = 1e-3;
    const panEndless =
      Math.abs(ptzLimits.pan.max - ptzLimits.pan.min - 360) < eps;
    return {
      left: panEndless ? false : ptz.pan <= ptzLimits.pan.min + eps,
      right: panEndless ? false : ptz.pan >= ptzLimits.pan.max - eps,
      up: ptz.tilt <= ptzLimits.tilt.min + eps,
      down: ptz.tilt >= ptzLimits.tilt.max - eps,
      zoomIn: ptz.zoom >= ptzLimits.zoom.max - eps,
      zoomOut: ptz.zoom <= ptzLimits.zoom.min + eps,
    };
  }, [ptz.pan, ptz.tilt, ptz.zoom, ptzLimits]);

  const atLimitRef = useRef(atLimit);
  atLimitRef.current = atLimit;

  const onPtzPressStart = useCallback(
    (dir: PtzDir) => {
      if (!ptzOperable) return;
      if (dir === "zoomIn" || dir === "zoomOut") return;
      if (atLimitRef.current[dir]) return;
      ptzContinuous.start(dir);
    },
    [ptzContinuous, ptzOperable],
  );

  const onPtzPressEnd = useCallback(() => {
    void ptzContinuous.stop();
  }, [ptzContinuous]);

  /** Preset mapping: always fire continuous zoom API (no atLimit gate on −/+). */
  const onZoomPressStart = useCallback(
    (dir: Extract<PtzDir, "zoomIn" | "zoomOut">) => {
      if (!ptzOperable) return;
      ptzContinuous.start(dir);
    },
    [ptzContinuous, ptzOperable],
  );

  const onZoomPressEnd = useCallback(() => {
    void ptzContinuous.stop();
  }, [ptzContinuous]);

  const onPtzResetZoom = useCallback(async () => {
    if (!ptzOperable || resetZoomInFlightRef.current) return;
    resetZoomInFlightRef.current = true;
    await ptzContinuous.stop();
    try {
      const next = await ptzResetZoom(cameraId);
      setPtz(next);
    } catch {
      /* ignore */
    } finally {
      resetZoomInFlightRef.current = false;
    }
  }, [cameraId, ptzContinuous, ptzOperable]);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "#000",
      }}
    >
      {relayLoading ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
            zIndex: 4,
          }}
        >
          <CircularProgress size={28} sx={{ color: "#93C5FD" }} />
          <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
            Starting PTZ live stream…
          </Typography>
        </Box>
      ) : null}

      {!relayLoading && relayError ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Typography
            sx={{ fontSize: 12, color: "#9CA3AF", textAlign: "center" }}
          >
            {relayError}
          </Typography>
        </Box>
      ) : null}

      {playlistUrl ? (
        <StreamVideo
          playlistUrl={playlistUrl}
          streamType="webrtc"
          enabled={Boolean(playlistUrl)}
          objectFit="contain"
          className="preset-mapping-ptz-stream"
          videoClassName="preset-mapping-ptz-stream__video"
          onStreamError={() => {
            setRelayError("PTZ live stream failed. Check relay / PTZ URL.");
            setStreamReady(false);
          }}
          onStreamLoadingChange={(loading) => {
            if (!loading) setStreamReady(true);
          }}
        />
      ) : null}

      {streamReady && ptzOperable ? (
        <div className="ds-preview-ptz-fs-overlay preset-mapping-ptz-controls">
          <PtzGamepadDpad
            placement="bottom-right"
            size="compact"
            counts={ptzContinuous.counts}
            isFull={ptzContinuous.isFull}
            atLimit={{
              left: atLimit.left,
              right: atLimit.right,
              up: atLimit.up,
              down: atLimit.down,
            }}
            operable={ptzOperable}
            onPressStart={onPtzPressStart}
            onPressEnd={onPtzPressEnd}
          />
          <div className="ds-preview-ptz-fs-zoom">
            <PtzZoomPanel
              inPlayer
              compact
              wheelZoom={false}
              ptz={ptz}
              ptzOperable={ptzOperable}
              sliderMin={sliderMin}
              sliderMax={sliderMax}
              onZoomCommit={onPtzZoomCommit}
              onResetZoom={() => void onPtzResetZoom()}
              onZoomPressStart={onZoomPressStart}
              onZoomPressEnd={onZoomPressEnd}
              atLimit={{ zoomIn: false, zoomOut: false }}
            />
          </div>
        </div>
      ) : null}
    </Box>
  );
}
